import { createColumnHelper } from "@tanstack/react-table";
import { T, useTranslate } from "@tolgee/react";
import { ExternalLink } from "lucide-react";
import type { UserFill } from "#/hooks/use-hyperliquid-account";
import { getHyperliquidExplorerTxUrl } from "#/lib/explorer";
import {
	formatDetailedDateTime,
	formatPrice,
	formatSignedUsdc,
	formatSize,
	formatUsdc,
} from "../../lib/formatters";

function RealizedPnlCell({ closedPnl, dir }: { closedPnl: number; dir: string }) {
	const { t } = useTranslate();
	const isOpen = dir.startsWith("Open");
	if (isOpen) {
		return (
			<span
				className="text-trade-text-faint"
				title={t("trades.realized-pnl-open-tooltip")}
			>
				—
			</span>
		);
	}
	return (
		<span
			className={`text-label2-tabular-medium ${
				closedPnl === 0
					? "text-trade-text-secondary"
					: closedPnl > 0
						? "text-trade-green"
						: "text-trade-red"
			}`}
		>
			{formatSignedUsdc(closedPnl)}
		</span>
	);
}

const col = createColumnHelper<UserFill>();

export const tradesColumns = [
	col.accessor("time", {
		id: "time",
		header: () => <T keyName="trades.columns.time" />,
		cell: ({ row, getValue }) => {
			const { hash } = row.original;
			const formatted = formatDetailedDateTime(getValue());
			if (!hash) {
				return (
					<span className="text-label2-tabular-medium text-trade-text-faint">
						{formatted}
					</span>
				);
			}
			return (
				<a
					href={getHyperliquidExplorerTxUrl(hash)}
					target="_blank"
					rel="noreferrer"
					aria-label="View transaction on Hyperliquid Explorer"
					className="inline-flex items-center gap-1 text-label2-tabular-medium text-trade-text-faint transition-colors hover:text-trade-text-strong"
				>
					{formatted}
					<ExternalLink className="h-3 w-3" />
				</a>
			);
		},
	}),
	col.accessor("coin", {
		header: () => <T keyName="trades.columns.market" />,
		enableSorting: false,
		cell: ({ getValue }) => (
			<span className="text-label2-medium text-trade-text-strong">
				{getValue()}-PERP
			</span>
		),
	}),
	col.accessor("side", {
		header: () => <T keyName="trades.columns.side" />,
		enableSorting: false,
		cell: ({ getValue }) => {
			const isBuy = getValue() === "B";
			return (
				<span className={isBuy ? "text-trade-green" : "text-trade-red"}>
					{isBuy ? <T keyName="general.buy" /> : <T keyName="general.sell" />}
				</span>
			);
		},
	}),
	col.accessor("px", {
		header: () => <T keyName="trades.columns.price" />,
		enableSorting: false,
		cell: ({ getValue }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatPrice(getValue())}
			</span>
		),
	}),
	col.accessor("sz", {
		header: () => <T keyName="trades.columns.size" />,
		enableSorting: false,
		cell: ({ getValue, row }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatSize(getValue())} {row.original.coin}
			</span>
		),
	}),
	col.display({
		id: "tradeValue",
		header: () => <T keyName="trades.columns.trade-value" />,
		cell: ({ row }) => {
			const value = Number(row.original.px) * Number(row.original.sz);
			return (
				<span className="text-label2-tabular-medium text-trade-text-secondary">
					{formatUsdc(value)}
				</span>
			);
		},
	}),
	col.accessor("fee", {
		header: () => <T keyName="trades.columns.fee" />,
		enableSorting: false,
		cell: ({ getValue }) => {
			const fee = Number(getValue());
			return (
				<span className="text-label2-tabular-medium text-trade-text-secondary">
					{fee < 0 ? formatSignedUsdc(fee) : formatUsdc(fee)}
				</span>
			);
		},
	}),
	col.accessor("closedPnl", {
		header: () => <T keyName="trades.columns.realized-pnl" />,
		enableSorting: false,
		cell: ({ getValue, row }) => (
			<RealizedPnlCell
				closedPnl={Number(getValue())}
				dir={row.original.dir}
			/>
		),
	}),
];
